import InputField from "@/components/Common/forms/InputField";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {useSettingsAgencyStore} from "@/store/agency";
import {FULL_DATE_FORMAT} from "@/store/constants";
import type {TSettingsScope} from "@/store/types";
import dayjs from "dayjs";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const AgencyAdminSettingsPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["agency_profile", "common", "settings"]);
  const isAdmin = useIsAdmin();
  const agency = useSettingsAgencyStore(state => state.agency);

  if (!isAdmin) return null;

  if (agency.id !== scope.id) {
    return (
      <div className="flex flex-col gap-8">
        <SkeletonLoader className="h-5 max-w-24" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-4 max-w-24" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-4 max-w-24" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-4 max-w-24" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:settings")}</span>
      <div className="flex flex-col gap-4">
        <InputField
          className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
          isDisabled
          label={t("agency_profile:agency_id")}
          labelClassName="text-neutral-black text-sm font-medium"
          placeholder={t("agency_profile:agency_id")}
          value={agency.id?.toString() ?? ""}
        />
        {!!agency.agency_owner_id && (
          <InputField
            className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
            isDisabled
            label={t("agency_profile:agency_owner_id")}
            labelClassName="text-neutral-black text-sm font-medium"
            placeholder={t("agency_profile:agency_owner_id")}
            value={agency.agency_owner_id.toString()}
          />
        )}
        <InputField
          className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
          isDisabled
          label={t("agency_profile:agency_registration_date")}
          labelClassName="text-neutral-black text-sm font-medium"
          placeholder={t("agency_profile:agency_registration_date")}
          value={agency.created ? dayjs(agency.created).format(FULL_DATE_FORMAT) : ""}
        />
      </div>
    </div>
  );
};

export default AgencyAdminSettingsPane;
